// Shared, serialized access-token acquisition for every authenticated call the
// SPA makes — the Functions API (apiScopes, via the global fetch interceptor
// in main.tsx). Previously also served direct browser-side Dataverse reads
// (dataverseScopes, via DataverseClient) — that layer was dead code and was
// removed (issue #19).
//
// Why this exists:
//
// Azure AD caps SPA refresh tokens at ~24 hours. Roughly a day after each
// interactive sign-in, acquireTokenSilent can no longer renew from a stored RT
// and falls back to a hidden prompt=none iframe against login.microsoftonline.com.
// When that iframe can't complete the silent SSO round-trip (the common
// third-party-cookie / lapsed-session case) MSAL throws BrowserAuthError
// `monitor_window_timeout` — and previously NOTHING recovered from it:
//   - the main.tsx interceptor swallowed it (request sent with no bearer -> 401);
//   - DataverseClient only handled InteractionRequiredAuthError, so it re-threw
//     monitor_window_timeout straight to the dashboard.
// With many dashboard queries firing in parallel, each spun ~6s on its own
// failing iframe before giving up — the "loads for a minute or two then times
// out" symptom.
//
// The fix mirrors ptc-statetax's authToken.ts:
//   1. Serialize per scope: one in-flight acquisition shared by all callers for
//      a given scope set, so we never fire N concurrent iframes (or N redirects).
//   2. Treat monitor_window_timeout / interaction_required as "needs interaction"
//      and renew with a full-page acquireTokenRedirect — top-level and
//      first-party, so it doesn't depend on the iframe (or third-party cookies)
//      working at all.
//   3. Loop-safe: redirect at most once per 30s window (a redirect just brought
//      us back — if silent still fails it isn't an expiry, so surface it instead
//      of bouncing forever).
//   4. Self-heal a stuck interaction.status flag (left over from a previously
//      interrupted redirect) by clearing MSAL's cache and starting a clean login,
//      so nobody has to clear site data by hand.

import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { saveReturnLocation } from './returnLocation';

// After an interactive redirect we record the time. If a silent acquisition
// still fails within this window the problem is not an expired token (a fresh
// interactive auth just completed) — redirecting again would loop, so we stop.
const INTERACTIVE_RETRY_WINDOW_MS = 30_000;
const INTERACTIVE_MARK_KEY = 'supervision.auth.lastInteractiveAt';

// One in-flight acquisition per scope set — distinct scope sets (e.g. apiScopes)
// never share a promise, but concurrent callers for the SAME scope set do.
const inFlight = new Map<string, Promise<string | null>>();

/**
 * Acquire an access token for the given scopes. Returns null when no token can
 * be attached (no signed-in account, a transient failure, or an interactive
 * redirect was triggered and the page is about to navigate away). Concurrent
 * callers for the same scopes share a single acquisition.
 */
export function getAccessToken(
  instance: IPublicClientApplication,
  scopes: string[],
): Promise<string | null> {
  const key = scopes.join(' ');
  let pending = inFlight.get(key);
  if (!pending) {
    pending = acquire(instance, scopes).finally(() => {
      inFlight.delete(key);
    });
    inFlight.set(key, pending);
  }
  return pending;
}

async function acquire(instance: IPublicClientApplication, scopes: string[]): Promise<string | null> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
  if (!account) return null;
  if (!instance.getActiveAccount()) instance.setActiveAccount(account);

  try {
    const result = await instance.acquireTokenSilent({ scopes, account });
    clearInteractiveMark();
    return result.accessToken;
  } catch (silentError) {
    if (needsInteraction(silentError)) {
      return acquireInteractively(instance, scopes, account, silentError);
    }
    // Transient (e.g. network) — let the caller / React Query retry rather than
    // yanking the user into a redirect over a blip.
    console.error('Silent token acquisition failed (will retry):', silentError);
    return null;
  }
}

async function acquireInteractively(
  instance: IPublicClientApplication,
  scopes: string[],
  account: AccountInfo,
  silentError: unknown,
): Promise<null> {
  if (interactiveAttemptedRecently()) {
    console.error('Token still unavailable after interactive auth:', silentError);
    return null;
  }
  markInteractiveAttempt();

  // SUP-02: an interactive redirect is a full top-level navigation that lands
  // back on the app root — stash the route the user is on so main.tsx can
  // restore it (history.replaceState) once handleRedirectPromise completes.
  saveReturnLocation();

  try {
    await instance.acquireTokenRedirect({ scopes, account });
  } catch (redirectError) {
    if (isInteractionInProgress(redirectError)) {
      // We serialize acquisition, so there is no concurrent redirect of ours; a
      // stuck interaction.status flag is left over from a previously interrupted
      // redirect — exactly the wedge that used to require a manual site-data
      // clear. Clear MSAL's cache and start a clean login so the user recovers
      // without touching browser settings.
      await instance.clearCache();
      // clearCache only clears MSAL's own keys, so the return-route stash above
      // survives; re-stash anyway in case a future MSAL version widens the clear.
      saveReturnLocation();
      await instance.loginRedirect({ scopes }).catch(() => undefined);
      return null;
    }
    console.error('Interactive token acquisition failed:', redirectError);
  }
  return null;
}

function needsInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (error instanceof BrowserAuthError) {
    // monitor_window_timeout: the hidden silent-renew iframe timed out (often a
    // 3rd-party-cookie / lapsed-session situation) and needs interaction.
    return ['interaction_required', 'monitor_window_timeout'].includes(error.errorCode);
  }
  return false;
}

function isInteractionInProgress(error: unknown): boolean {
  return error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress';
}

function interactiveAttemptedRecently(): boolean {
  const raw = sessionStorage.getItem(INTERACTIVE_MARK_KEY);
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < INTERACTIVE_RETRY_WINDOW_MS;
}

function markInteractiveAttempt(): void {
  sessionStorage.setItem(INTERACTIVE_MARK_KEY, String(Date.now()));
}

function clearInteractiveMark(): void {
  sessionStorage.removeItem(INTERACTIVE_MARK_KEY);
}
